import { ArrowDown, MapPin, Plus, Quote } from "lucide-react";
import { REGISTER_URL } from "../data/content";
import { Picture } from "./Picture";

export function Hero(): JSX.Element {
  return (
    <section
      aria-labelledby="hero-title"
      className="relative overflow-hidden pb-20 pt-12 sm:pb-28 sm:pt-20 lg:pt-24"
    >
      {/* Soft organic background — three blurred ellipses with editorial restraint */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 -right-24 h-[460px] w-[460px] rounded-full bg-tertiary-fixed/55 blur-3xl" />
        <div className="absolute top-40 -left-32 h-[380px] w-[380px] rounded-full bg-primary-fixed/35 blur-3xl" />
        <div className="absolute -bottom-32 right-1/4 h-[300px] w-[300px] rounded-full bg-secondary-fixed/35 blur-3xl" />
      </div>

      <div className="container-welope grid grid-cols-1 gap-lg lg:grid-cols-12 lg:gap-xl">
        <div className="bloom-on-scroll lg:col-span-7 lg:pr-4">
          <p className="section-eyebrow">Made in Germany · DSGVO-konform</p>

          <h1
            id="hero-title"
            className="mt-7 font-headline text-[44px] font-extrabold leading-[1.04] tracking-[-0.03em] text-primary sm:text-[64px] sm:leading-[1.02] lg:text-[80px] lg:leading-[1.0]"
          >
            Das Zuhause
            <br />
            deines Lieblings
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">im Internet.</span>
              <span
                aria-hidden
                className="absolute -bottom-1 left-0 right-0 h-2 origin-left rounded-full bg-secondary"
              />
            </span>
          </h1>

          <p className="mt-8 max-w-xl font-body text-body-lg leading-[1.55] text-on-surface-variant">
            Welope ist das soziale Netzwerk für Haustiere. Eigene Profile für Hunde, Katzen
            &amp; Co., verbunden mit einer Community, die Tiere genauso liebt wie du.
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a href={REGISTER_URL} className="btn-primary text-base">
              Auf die Warteliste
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </a>
            <a href="#so-gehts" className="btn-secondary text-base">
              So funktioniert's
            </a>
          </div>

          <p className="mt-10 font-body text-label-md text-on-surface-variant">
            {TRUST_BADGES.join(" · ")}
          </p>
        </div>

        {/* Editorial photo collage */}
        <div className="bloom-on-scroll relative lg:col-span-5" style={{ transitionDelay: "120ms" }}>
          <HeroCollage />
        </div>
      </div>
    </section>
  );
}

/** Editorial copy kept tight at the call site so the hero reads as one object. */
const TRUST_BADGES = ["Kein Datenverkauf", "Self-hosted in DE"] as const;

function HeroCollage(): JSX.Element {
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[520px]">
      {/* Background tint stack — three depth layers, no harsh gradients */}
      <div
        aria-hidden
        className="absolute inset-4 rounded-[2rem] bg-gradient-to-br from-primary-fixed via-tertiary-fixed to-secondary-fixed/45 shadow-bloom"
      />
      <div
        aria-hidden
        className="absolute inset-8 rounded-3xl bg-gradient-to-br from-surface-container-lowest/60 via-transparent to-primary-fixed/30 blur-[2px]"
      />

      {/* Hero photo — welp / hound */}
      <div className="absolute inset-x-8 top-8 overflow-hidden rounded-[1.75rem] shadow-bloom-lg ring-1 ring-primary-fixed/40">
        <div className="aspect-[4/5] w-full">
          <Picture
            src="/images/hero-dog.jpg"
            alt="Welpe einer Französischen Bulldogge in gelbem Pullover, genüsslich blinzelnd"
            width={900}
            height={601}
            priority
            className="h-full w-full object-cover"
          />
        </div>
      </div>

      {/* Floating "pet profile" mini-card */}
      <div className="absolute -bottom-4 left-2 w-64 max-w-[80%] rounded-2xl bg-surface-container-lowest p-4 shadow-bloom-lg ring-1 ring-outline-variant/30 sm:left-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 overflow-hidden rounded-full border-2 border-primary-fixed bg-surface-container-high">
            <Picture
              src="/images/hero-avatar.jpg"
              alt=""
              width={96}
              height={96}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="min-w-0">
            <p className="font-headline text-headline-md leading-tight text-on-surface">Bruno</p>
            <p className="flex items-center gap-1 font-body text-label-sm text-on-surface-variant">
              <MapPin className="h-3 w-3" aria-hidden="true" />
              280 m entfernt · Golden Retriever
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-surface-variant/40 pt-3">
          <div className="flex -space-x-2">
            {["#9d4320", "#173124", "#fd8c63"].map((c, i) => (
              <span
                key={c}
                aria-hidden
                className="h-6 w-6 rounded-full border-2 border-surface-container-lowest"
                style={{ backgroundColor: c, zIndex: 3 - i }}
              />
            ))}
          </div>
          <span className="font-body text-label-sm text-secondary">142 Follower</span>
        </div>
      </div>

      {/* Floating "im Park jetzt" mini-pill — richer, with a green pulse dot */}
      <div className="absolute -right-2 top-12 hidden w-60 rounded-2xl bg-primary p-4 text-on-primary shadow-bloom-lg sm:block">
        <p className="flex items-center gap-2 font-body text-label-sm uppercase tracking-wider opacity-90">
          <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-tertiary-fixed" />
          <Quote className="h-3.5 w-3.5" aria-hidden="true" />
          Im Park jetzt
        </p>
        <p className="mt-1 font-headline text-headline-md leading-tight">6 Hunde</p>
        <p className="mt-2 font-body text-body-sm opacity-90">
          Spaziergang? Wir sehen uns um 17 Uhr am Hexenweiher.
        </p>
      </div>

    </div>
  );
}

// `Plus` is reserved for future variants; re-export keeps the import stable.
export const _heroReserved = Plus;
